<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from '../../../../index';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      channelName: 'SMS',
      authMode: 'api_id',
      apiId: '',
      login: '',
      password: '',
      phoneNumber: '',
      senderName: '',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    useApiId() {
      return this.authMode === 'api_id';
    },
  },
  validations() {
    const base = {
      channelName: { required },
      phoneNumber: { required },
    };
    if (this.authMode === 'api_id') {
      return { ...base, apiId: { required } };
    }
    return { ...base, login: { required }, password: { required } };
  },
  methods: {
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      try {
        const channelParams = {
          type: 'smsc',
          phone_number: this.phoneNumber.startsWith('+')
            ? this.phoneNumber
            : `+${this.phoneNumber.replace(/\D/g, '')}`,
          sender_name: this.senderName || undefined,
        };

        if (this.authMode === 'api_id') {
          channelParams.api_id = this.apiId;
        } else {
          channelParams.login = this.login;
          channelParams.password = this.password;
        }

        const smscChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            name: this.channelName?.trim(),
            channel: channelParams,
          }
        );

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: smscChannel.id,
          },
        });
      } catch (error) {
        useAlert(
          error.message ||
            'Error creating SMSC.ru channel. Please check your credentials.'
        );
      }
    },
  },
};
</script>

<template>
  <form class="flex flex-wrap flex-col mx-0" @submit.prevent="createChannel()">
    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.channelName.$error }">
        Channel name
        <input
          v-model="channelName"
          type="text"
          placeholder="e.g. SMS Russia"
          @blur="v$.channelName.$touch"
        />
        <span v-if="v$.channelName.$error" class="message">
          This field is required
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0 mt-4">
      <label>
        Authentication mode
        <select v-model="authMode">
          <option value="api_id">API ID</option>
          <option value="login_password">Login + Password</option>
        </select>
      </label>
    </div>

    <div v-if="useApiId" class="flex-shrink-0 flex-grow-0 mt-4">
      <label :class="{ error: v$.apiId?.$error }">
        API ID *
        <input
          v-model="apiId"
          type="text"
          placeholder="Your SMSC.ru API ID"
          @blur="v$.apiId?.$touch"
        />
        <span v-if="v$.apiId?.$error" class="message">
          This field is required
        </span>
      </label>
    </div>

    <template v-if="!useApiId">
      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <label :class="{ error: v$.login?.$error }">
          Login *
          <input
            v-model="login"
            type="text"
            placeholder="SMSC.ru login (phone number)"
            @blur="v$.login?.$touch"
          />
          <span v-if="v$.login?.$error" class="message">
            This field is required
          </span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <label :class="{ error: v$.password?.$error }">
          Password *
          <input
            v-model="password"
            type="password"
            placeholder="SMSC.ru password"
            @blur="v$.password?.$touch"
          />
          <span v-if="v$.password?.$error" class="message">
            This field is required
          </span>
        </label>
      </div>
    </template>

    <div class="flex-shrink-0 flex-grow-0 mt-4">
      <label :class="{ error: v$.phoneNumber.$error }">
        Virtual phone number *
        <input
          v-model="phoneNumber"
          type="text"
          placeholder="+79770001234"
          @blur="v$.phoneNumber.$touch"
        />
        <span v-if="v$.phoneNumber.$error" class="message">
          This field is required
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0 mt-4">
      <label>
        Sender name (optional)
        <input
          v-model="senderName"
          type="text"
          placeholder="e.g. MyCompany"
        />
        <span class="text-xs text-slate-500">
          Must be approved in your SMSC.ru account
        </span>
      </label>
    </div>

    <div class="w-full mt-4">
      <NextButton
        :is-loading="uiFlags.isCreating"
        type="submit"
        solid
        blue
        label="Create SMSC.ru channel"
      />
    </div>
  </form>
</template>
